/**
 * Phone MANAGE PARTY (DESIGN-CHANGELOG 2026-09-03; `design/GT Mobile
 * Crew.dc.html:216-360`). AA phase 5 §K.
 *
 * The sticky bar (`◂ PARTY` · MANAGE · the N WAITING chip rides the bar only),
 * then the panels the PARTY screen used to carry inline, stacked, moved here
 * whole so no write path changed. Leaders only — see the desktop twin.
 */

import { useEffect } from 'react';
import type { CSSProperties } from 'react';

import { Avatar } from '../components/Avatar.tsx';
import { PixelButton } from '../components/PixelButton.tsx';
import InvitePanel from '../sheets/InvitePanel.tsx';
import { useQd } from '../store/index.ts';
import { crewVM, ownerVM } from '../store/selectors.ts';

const FIELD: CSSProperties = {
  background: 'var(--gt-field)',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '11.5px',
  padding: '12px',
  outline: 'none',
};

const CHIP: CSSProperties = {
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text-3)',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '9.5px',
  padding: '12px 10px',
  cursor: 'pointer',
};

export default function Manage(): JSX.Element {
  const s = useQd();
  const v = crewVM(s);

  useEffect(() => {
    if (v.meAdmin) {
      void s.loadPending();
      void s.loadJoinLink();
      void s.loadJoinRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v.meAdmin]);

  return (
    <div
      data-screen-label="Manage party"
      style={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px 14px 26px' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid var(--gt-line)', background: 'var(--gt-panel)', position: 'sticky', top: 0, zIndex: 20, margin: '-16px -14px 0' }}>
        <div
          onClick={() => s.nav('crew')}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 44, padding: '0 11px', border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-4)', fontFamily: 'var(--gt-font-mono)', fontSize: '9.5px', letterSpacing: '.05em', cursor: 'pointer', boxSizing: 'border-box' }}
        >
          ◂ PARTY
        </div>
        <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '9.5px', color: 'var(--gt-text)', transform: 'translateY(0.13em)' }}>MANAGE</span>
        <div style={{ flex: 1 }} />
        {v.hasJqWait ? (
          <span style={{ fontFamily: 'var(--gt-font-mono)', fontSize: 8, letterSpacing: '.08em', color: 'var(--acc)', border: '1px solid var(--acc)', padding: '3px 6px' }}>{v.jqWaitTxt}</span>
        ) : null}
      </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            border: '1px solid var(--gt-border)',
            background: 'var(--gt-panel)',
            padding: '14px 12px 18px',
          }}
        >
          {/* The mirror's header row. The phone drops the party name from the
              title — the column is too narrow for it — and keeps it on the
              line below, which names the party anyway. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '7.5px', color: 'var(--gt-text)' }}>
              MANAGE PARTY
            </span>
            <div style={{ flex: 1 }} />
            <span
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '8px',
                letterSpacing: '.06em',
                padding: '3px 7px',
                border: '1px solid ' + v.myRoleClr,
                color: v.myRoleClr,
                whiteSpace: 'nowrap',
              }}
            >
              YOU — {v.myRoleLbl}
            </span>
          </div>
          <div
            style={{
              marginTop: -6,
              fontFamily: 'var(--gt-font-mono)',
              fontSize: '8.5px',
              lineHeight: 1.7,
              letterSpacing: '.04em',
              color: 'var(--gt-muted-2)',
            }}
          >
            {v.crewMetaTxt}
          </div>

          {/* CREW NAME — first thing in the block (Marcus, 2026-08-21: "just
              put it at the top of the manage crew block in the friends view for
              now"). Owner-only; migration 3030 checks that again.

              The SLUG does not move with it. It is in every invitation link
              already sent and in members' bookmarks, so the crew's address stays
              what `crewMetaTxt` above says even after the label changes — see
              `Repository.renameCrew`.

              Not in the mirrors at all; a DESIGN-CHANGELOG entry is owed. */}
          {v.canRename ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span
                style={{
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: '8.5px',
                  letterSpacing: '.08em',
                  color: 'var(--gt-muted-2)',
                }}
              >
                PARTY NAME
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                <input
                  value={v.crewNameDraft}
                  onChange={(e) => s.setCrewNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.repeat) void s.renameCrew();
                  }}
                  maxLength={40}
                  placeholder="PARTY NAME"
                  style={{ ...FIELD, flex: 1, minWidth: 0 }}
                />
                <PixelButton
                  label={v.renameLbl}
                  variant="outline"
                  /* Dimmed while there is nothing to save, so the control says
                     "no change" rather than offering a write that does nothing. */
                  busy={v.renameBusy}
                  disabled={v.renameLocked || !v.renameDirty}
                  onClick={() => void s.renameCrew()}
                  fs={8}
                  pad="0 14px"
                  center
                  nowrap
                />
              </div>
            </div>
          ) : null}

          {/* JOIN LINK — the shareable door (3056, docs/JOIN-LINKS.md). Above
              REQUESTS because it is what produces them. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div
              style={{
                fontFamily: 'var(--gt-font-pixel)',
                fontSize: '7px',
                letterSpacing: '.08em',
                color: 'var(--gt-muted)',
              }}
            >
              {v.jlHdr}
            </div>
            <span
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '9px',
                lineHeight: 1.8,
                letterSpacing: '.05em',
                color: 'var(--gt-muted-2)',
              }}
            >
              {v.jlLive ? v.jlOnTxt : v.jlOffTxt}
            </span>
            {/* THE URL EXISTS FOR ONE RENDER. The server keeps only a hash and
                genuinely cannot return it again, so there is no "show me the
                link" affordance to build — rotating is the only way back. */}
            {/* THE LINK ITSELF, shown whenever there is one (3057). It used to
                appear only on the render that minted it, which meant the only
                way to share it a second time was NEW LINK — killing the copy
                already sitting in the chat it was posted to. */}
            {v.jlUrl ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '7px',
                  border: '1px dashed var(--gt-border-2)',
                  padding: '11px 12px',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--gt-font-mono)',
                    fontSize: '10px',
                    color: 'var(--gt-text)',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {v.jlUrl}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span
                    onClick={() => void navigator.clipboard?.writeText(v.jlUrl)}
                    style={CHIP}
                  >
                    COPY
                  </span>
                </div>
              </div>
            ) : v.jlUnreadable ? (
              /* Live, but minted before the token was stored. Say why rather
                 than showing a blank space where a link should be. */
              <span
                style={{
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: '9px',
                  lineHeight: 1.8,
                  letterSpacing: '.05em',
                  color: 'var(--acc2)',
                }}
              >
                {v.jlUnreadableTxt}
              </span>
            ) : null}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <PixelButton
                label={v.jlNewLbl}
                busy={v.jlBusy}
                onClick={() => void s.newJoinLink()}
                fs={8}
                pad="14px 14px"
                nowrap
              />
              {v.jlLive ? (
                <span
                  onClick={() => {
                    if (!v.jlBusy) void s.offJoinLink();
                  }}
                  style={{ ...CHIP, color: 'var(--acc2)' }}
                >
                  {v.jlOffLbl}
                </span>
              ) : null}
            </div>
            {v.jlLive ? (
              <span
                style={{
                  fontFamily: 'var(--gt-font-mono)',
                  fontSize: '9px',
                  lineHeight: 1.8,
                  letterSpacing: '.05em',
                  color: 'var(--gt-muted-2)',
                }}
              >
                {v.jlRotateWarn}
              </span>
            ) : null}
          </div>

          {/* REQUESTS — above PENDING and outside every count, for the reason
              PENDING is: Marcus's "invites should not count as joined" applies
              identically to somebody who has only asked. */}
          {v.hasRequests ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div
                style={{
                  fontFamily: 'var(--gt-font-pixel)',
                  fontSize: '7px',
                  letterSpacing: '.08em',
                  color: 'var(--gt-muted)',
                }}
              >
                {v.requestsHdr}
              </div>
              {v.requestRows.map((r) => (
                <div
                  key={r.key}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '7px',
                    border: '1px dashed var(--gt-border-2)',
                    padding: '11px 12px',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '11px',
                      color: 'var(--gt-text)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {r.name}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '9px',
                      letterSpacing: '.05em',
                      color: 'var(--gt-muted)',
                    }}
                  >
                    {r.whenTxt}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span
                      onClick={() => {
                        if (!r.busy) void s.decideRequest(r.id, 'approve');
                      }}
                      style={{
                        ...CHIP,
                        ...(r.busy ? { color: 'var(--gt-muted)', cursor: 'progress' } : null),
                      }}
                    >
                      {r.approveLbl}
                    </span>
                    <span
                      onClick={() => {
                        if (!r.busy) void s.decideRequest(r.id, 'deny');
                      }}
                      style={{
                        ...CHIP,
                        color: 'var(--acc2)',
                        ...(r.busy ? { cursor: 'progress' } : null),
                      }}
                    >
                      {r.denyLbl}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {/* ONE FIELD (3026, docs/INVITES.md). The NAME box is gone because
              picking somebody else's display name was never ours to do — they
              choose their own when they join — and with it went the temp
              password, because no account exists until they accept.

              No role select either: a crew has exactly one owner and everybody
              else is a player (D4), so there was never a second value to pick.

              The mirrors still draw NAME and `EMAIL (OPTIONAL)`; a
              DESIGN-CHANGELOG entry is owed upstream for both that and this
              (X-HANDOFF §2.5, docs/INVITES.md §5). */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <input
              value={v.invEmail}
              onChange={(e) => s.setInvEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.repeat) void s.sendInvite();
              }}
              type="email"
              placeholder="EMAIL ADDRESS"
              style={FIELD}
            />
            <span
              style={{
                fontFamily: 'var(--gt-font-mono)',
                fontSize: '9px',
                lineHeight: 1.8,
                letterSpacing: '.05em',
                color: 'var(--gt-muted-2)',
              }}
            >
              {v.inviteNote}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <PixelButton
                label={v.inviteLbl}
                busy={v.invBusy}
                onClick={() => void s.sendInvite()}
                fs={8}
                pad="14px 14px"
                nowrap
              />
            </div>
          </div>

          {/* PENDING — above the members and outside every count, because an
              invitation is not a membership. Expired rows STAY: expiry does not
              close an invitation, it only makes it unusable, so RESEND is the
              way out and the owner can still see who never answered. */}
          {v.hasPending ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div
                style={{
                  fontFamily: 'var(--gt-font-pixel)',
                  fontSize: '7px',
                  letterSpacing: '.08em',
                  color: 'var(--gt-muted)',
                }}
              >
                {v.pendingHdr}
              </div>
              {v.pendingRows.map((p) => (
                <div
                  key={p.key}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '7px',
                    border: '1px dashed var(--gt-border-2)',
                    padding: '11px 12px',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '11px',
                      color: 'var(--gt-text)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {p.email}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '9px',
                      letterSpacing: '.05em',
                      color: p.clr,
                    }}
                  >
                    {p.whenTxt} · {p.deliveryTxt}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span
                      onClick={() => {
                        if (!p.busy) void s.sendInvite(p.email);
                      }}
                      style={{
                        ...CHIP,
                        ...(p.busy ? { color: 'var(--gt-muted)', cursor: 'progress' } : null),
                      }}
                    >
                      {p.resendLbl}
                    </span>
                    <span
                      onClick={() => {
                        if (!p.busy) void s.cancelInvite(p.email);
                      }}
                      style={{
                        ...CHIP,
                        color: 'var(--acc2)',
                        ...(p.busy ? { cursor: 'progress' } : null),
                      }}
                    >
                      CANCEL
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          {v.hasInvite ? <InvitePanel /> : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {v.adminRows.map((u) => (
              <div
                key={u.key}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '9px',
                  border: '1px solid var(--gt-line)',
                  background: 'var(--gt-card)',
                  padding: '10px 11px',
                }}
              >
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <Avatar size={30} fs={8} ini={u.ini} bg={u.hueBg} hasAv={u.hasAv} avBg={u.avBg} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0, flex: 1 }}>
                    <span style={{ fontWeight: 600, fontSize: '12.5px', color: 'var(--gt-text)' }}>{u.name}</span>
                    {/* The address they SIGN IN WITH (migration 3029), not the
                        retired `profiles.recovery_email` this line used to
                        read. When an account still has the synthesized
                        placeholder it says so in the alert tone, because that
                        person cannot recover a forgotten password by any route
                        — RESET PW is gone and FORGOT PASSWORD needs a real
                        address (docs/EMAIL.md). */}
                    <span
                      style={{
                        fontFamily: 'var(--gt-font-mono)',
                        fontSize: '9px',
                        color: u.emailClr,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {u.emailTxt}
                    </span>
                  </div>
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '8.5px',
                      color: u.statusClr,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {u.statusTxt}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* The role SELECT is gone (DESIGN-CHANGELOG 2026-08-18
                      "CO-ADMIN removed"): roles follow who owns the crew, so
                      there is nothing to pick. It also could not work — phase B
                      retired `op=role`, and because `mutate()` toasts before it
                      commits, every change said ROLE UPDATED and then silently
                      snapped back. A read-only chip states the fact instead. */}
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '9.5px',
                      letterSpacing: '.04em',
                      color: u.roleClr,
                      border: '1px solid ' + u.roleBd,
                      padding: '11px 9px',
                    }}
                  >
                    {u.roleTxt}
                  </span>
                  {/* COPY LINK and RESET PW are gone (Marcus, 2026-08-21).
                      Both were temp-password-world controls and both had
                      already stopped telling the truth:

                      COPY INVITE built the LEGACY `/join#user=` link, which the
                      3026 flow does not use — an invitation is an emailed token
                      now, and the owner never holds one.

                      RESET PW gated on `!u.email`, which is the RECOVERY column.
                      Every member who joined by invitation has their real
                      address as their LOGIN and no recovery address, so the chip
                      rendered for exactly the people the server then refuses
                      (`hasOwnIdentity`). A control that appears and cannot work. */}
                  {u.canRm ? (
                    <span
                      onClick={() => {
                        if (s.rmArm === u.key) void s.doRm(u.key);
                        else s.setAdminForm({ rmArm: u.key });
                      }}
                      style={{ ...CHIP, border: '1px solid ' + u.rmBd, color: u.rmClr }}
                    >
                      {u.rmTxt}
                    </span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      {ownerVM(s).show ? <OwnerControls /> : null}
    </div>
  );
}

/**
 * OWNER CONTROLS. `design/GT Mobile Crew.dc.html` lines 257-274 — the desktop's
 * two columns stacked into two full-width blocks, each button a 44px tap
 * target, and the subtitle shortened to `OWNER ONLY` for the narrow header.
 *
 * The mirror's trailing `✓ {{ trMsg }}` row is NOT ported, for the same reason
 * as desktop: it sits inside this owner gate and the transfer demotes you, so
 * nothing renders it. `handOver` toasts instead.
 */
function OwnerControls(): JSX.Element {
  const s = useQd();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--gt-border)',
        background: 'var(--gt-panel)',
      }}
    >
      <div
        style={{
          background: 'var(--gt-raised)',
          borderBottom: '1px solid var(--gt-border)',
          padding: '11px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--gt-font-pixel)',
            fontSize: '7.5px',
            color: 'var(--gt-gold)',
            transform: 'translateY(0.13em)',
          }}
        >
          LEADER CONTROLS
        </span>
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '8.5px', color: 'var(--gt-muted-2)' }}>
          LEADER ONLY
        </span>
      </div>

      <div style={{ ...OWN_BLOCK, borderBottom: '1px solid var(--gt-line)' }}>
        <span
          onClick={() => s.openOwn('transfer')}
          style={{ ...OWN_BTN, borderColor: 'var(--gt-border-2)', color: 'var(--gt-text-3)' }}
        >
          HAND OVER THE PARTY ▸
        </span>
        <span style={OWN_NOTE}>
          HANDS OVER EVERYTHING — YOU BECOME A REGULAR PLAYER. EXACTLY ONE LEADER AT ALL TIMES.
        </span>
      </div>

      <div style={OWN_BLOCK}>
        <span
          onClick={() => s.openOwn('delete')}
          style={{ ...OWN_BTN, borderColor: 'var(--acc2)', color: 'var(--acc2)' }}
        >
          DISBAND PARTY ✕
        </span>
        <span style={OWN_NOTE}>
          REMOVES THE BOARD, CALLS, BALLOTS, VIDEOS AND EVERY MEMBERSHIP. ACCOUNTS SURVIVE. OLD
          INVITE LINKS DIE — THE SLUG IS NOT RECYCLED.
        </span>
      </div>
    </div>
  );
}

const OWN_BLOCK: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 9,
  padding: '13px 12px',
};

/** 44px, because the mirror centres these rather than shrink-wrapping them. */
const OWN_BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 44,
  border: '1px solid',
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '10px',
  cursor: 'pointer',
  boxSizing: 'border-box',
};

const OWN_NOTE: CSSProperties = {
  fontFamily: 'var(--gt-font-mono)',
  fontSize: '8px',
  lineHeight: 1.7,
  letterSpacing: '.04em',
  color: 'var(--gt-muted-2)',
};
